<template>
  <div class="gs-asset-type gs-facebook-ads">
    <div class="gs-asset-split">
      <template v-for="(ad, index) in facebookAds">
        <section
          v-if="isFacebookAdTabActive(ad)"
          :key="ad.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="ad.name"
                  :validator="$v.localFunnelResource.facebook_ads.$each[index].name"
                  :attribute="$t('frontend_groovesell_facebook_ad_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="ad.enabled" :value="ad.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-facebook-ad"
                title="Delete Facebook ad"
                aria-label="Delete Facebook ad"
                @click="openConfirmModal(index)"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.facebook_ads.$each[index].copy"
              :attribute="$t('frontend_resources_ad_copy')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_ad_copy')" :error="message">
                  <textarea
                    v-model="ad.copy"
                    class="gs-textarea"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_copy')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(ad.copy) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="adCopyInsertActions(index)" size="action" />
            </div>
          </div>

          <div class="gs-asset-editor__section">
            <gs-field
              :label="$t('frontend_resources_suggested_interests')"
              hint="Press Enter to add an interest. Affiliates copy these into their Facebook audience."
            >
              <div class="gs-facebook-ads__tags">
                <vue-tags-input
                  v-model="interestsInput"
                  :tags="interestTags(ad)"
                  :placeholder="$t('frontend_resources_suggested_interests')"
                  @blur="commitPendingInterest(index)"
                  @tags-changed="setInterests(index, $event)"
                />
              </div>
            </gs-field>
          </div>

          <div class="gs-asset-editor__section">
            <span class="gs-asset-editor__label">{{ $t('frontend_resources_ad_images') }}</span>
            <div class="gs-facebook-ads__images">
              <figure
                v-for="(image, imageIndex) in (ad.images || [])"
                :key="imageIndex"
                class="gs-facebook-ads__image"
              >
                <img :src="image" :alt="`${ad.name} creative ${imageIndex + 1}`" />
                <button
                  type="button"
                  class="gs-facebook-ads__image-remove"
                  data-action="delete-ad-image"
                  :title="$t('frontend_resources_delete_image')"
                  :aria-label="$t('frontend_resources_delete_image')"
                  @click="deleteAdImage(index, imageIndex)"
                >
                  <gs-icon name="x" size="14"></gs-icon>
                </button>
              </figure>

              <button
                type="button"
                class="gs-facebook-ads__image-add"
                data-action="add-ad-image"
                @click="addNewFacebookAdImage(index)"
              >
                <gs-icon name="plus" size="18"></gs-icon>
                <span>Add image</span>
              </button>
            </div>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasFacebookAds && !selectedFacebookAd" class="gs-asset-empty">
        <p>Select a Facebook ad from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Facebook ads"
        :items="facebookAds"
        :selected-id="selectedFacebookAdId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled ad"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_fb_ads')"
        enabled-key="enabled"
        :error-ids="facebookAdsErrorIds"
        @select="selectFacebookAd"
        @add="createFacebookAd()"
        @reorder="reorderFacebookAds"
      />
    </div>

    <gs-named-modal
      ref="adImagesModal"
      class="gs-app"
      name="image-picker"
      :title="$t('frontend_resources_ad_images')"
      width="100%"
      height="100%"
    >
      <image-picker-without-modal
        :value="imagePickerVal"
        modal="true"
        remove="false"
        @close-modal="$refs.adImagesModal.close()"
        @selected="addFacebookAdImageToArray"
      ></image-picker-without-modal>
    </gs-named-modal>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this Facebook ad?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteFacebookAd()"
    />
  </div>
</template>

<script>
import ImagePickerWithoutModal from '@groovepages/gd-ui-kit/components/ImagePicker';
import VueTagsInput from '@johmun/vue-tags-input';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import FieldError from './FieldError.vue';
import { toTabOrder, sortByTabOrder, idFromTabValue } from './tabOrder';

export default {
  mixins: [generateNumberMixin],
  components: {
    AssetItemList,
    FieldError,
    ImagePickerWithoutModal,
    VueTagsInput,
    TrashIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    facebookAdsTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      facebookAdId: null,
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localFacebookAdsTab: this.facebookAdsTab,
      imagePickerVal: '',
      selectedFacebookAdsImageIndex: '',
      // Half-typed interest for the tag input; cleared whenever a different ad
      // is opened so it never leaks between ads.
      interestsInput: '',
    }
  },
  computed: {
    facebookAds() {
      return (this.localFunnelResource && this.localFunnelResource.facebook_ads) || [];
    },
    hasFacebookAds() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.facebook_ads &&
        this.localFunnelResource.facebook_ads.length
      );
    },
    selectedFacebookAd() {
      return this.facebookAds.filter((ad) => this.isFacebookAdTabActive(ad))[0] || null;
    },
    selectedFacebookAdId() {
      if (!this.localFacebookAdsTab) return null;
      return idFromTabValue('facebook_ad', this.localFacebookAdsTab);
    },
    facebookAdsErrorIds() {
      let facebookAdsErrorIds = [];

      for (const index in this.$v.localFunnelResource.facebook_ads.$each.$iter) {
        if (this.$v.localFunnelResource.facebook_ads.$each.$iter[index].$error) {
          facebookAdsErrorIds.push(
            this.$v.localFunnelResource.facebook_ads.$each.$iter[index].$model.id
          );
        }
      }

      return facebookAdsErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    characterCount(value) {
      const length = (value || '').length;
      return `${length} ${length === 1 ? 'character' : 'characters'}`;
    },
    // `interests` is stored as the comma-separated string affiliates already
    // see, so the tag input only changes how it is entered, not what is saved.
    interestTags(ad) {
      return String(ad.interests || '')
        .split(/[,\n]/)
        .map((interest) => interest.trim())
        .filter((interest) => interest.length)
        .map((interest) => ({ text: interest }));
    },
    setInterests(index, tags) {
      this.localFunnelResource.facebook_ads[index].interests = tags
        .map((tag) => tag.text)
        .join(', ');
      this.interestsInput = '';

      this.updateFunnelResource();
    },
    // An interest the merchant typed but never pressed Enter on would be lost
    // on Save, so leaving the field commits it just as Enter would.
    commitPendingInterest(index) {
      const pending = this.interestsInput.trim();
      if (!pending) return;

      const ad = this.localFunnelResource.facebook_ads[index];
      const tags = this.interestTags(ad);
      if (tags.some((tag) => tag.text === pending)) {
        this.interestsInput = '';
        return;
      }

      this.setInterests(index, tags.concat([{ text: pending }]));
    },
    affiliateLinksActions(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToAdCopy(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    adCopyInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToAdCopy(index),
        },
        ...this.affiliateLinksActions(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectFacebookAd(id) {
      this.interestsInput = '';
      this.handleFacebookAdsTabChange(`facebook_ad_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handler speaks
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderFacebookAds(ids) {
      this.handleFacebookAdsTabOrderUpdate(toTabOrder('facebook_ad', ids));
    },
    handleFacebookAdsTabChange(newTab) {
      this.localFacebookAdsTab = newTab || this.localFacebookAdsTab;
      this.$emit('handleFacebookAdsTabChange', this.localFacebookAdsTab);
    },
    handleFacebookAdsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.facebook_ads, 'facebook_ad', updatedTabs);

      this.updateFunnelResource();
    },
    createFacebookAd() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.facebook_ads.length + 1;

      this.localFunnelResource.facebook_ads.push({
        id: newPostId,
        name: `${this.$t('facebook_ad')} ${newPostCount}`,
        copy: '',
        interests: '',
        images: [],
      });

      this.interestsInput = '';
      this.localFacebookAdsTab = `facebook_ad_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleFacebookAdsTabChange();
    },
    openConfirmModal(facebookAdId) {
      this.facebookAdId = facebookAdId;
      this.confirmOpen = true;
    },
    async deleteFacebookAd() {
      this.localFunnelResource.facebook_ads.splice(this.facebookAdId, 1);
      this.localFacebookAdsTab = '';

      if (this.localFunnelResource.facebook_ads.length > 0) {
        let nextOpenIndex = 0;
        if (this.facebookAdId != 0) {
          nextOpenIndex = this.facebookAdId - 1;
        }

        const nextOpenId = this.localFunnelResource.facebook_ads[nextOpenIndex].id;
        this.localFacebookAdsTab = `facebook_ad_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleFacebookAdsTabChange();
    },
    addAffiliateNameToAdCopy(index) {
      this.localFunnelResource.facebook_ads[
        index
      ].copy = `${this.localFunnelResource.facebook_ads[index].copy}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToAdCopy(index, landingPageId) {
      this.localFunnelResource.facebook_ads[
        index
      ].copy = `${this.localFunnelResource.facebook_ads[index].copy}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    addNewFacebookAdImage(index) {
      this.selectedFacebookAdsImageIndex = index;
      this.$refs.adImagesModal.open();
    },
    deleteAdImage(index, imageIndex) {
      this.localFunnelResource.facebook_ads[index].images.splice(imageIndex, 1);

      this.updateFunnelResource();
    },
    // The image library emits two different payloads: picking an existing
    // image emits the URL string (and `close-modal`), while uploading emits
    // the whole image record and never emits `close-modal`. Creatives are
    // stored as URL strings (`<img :src="image">`), so normalise the payload
    // and close the picker from here for both paths.
    addFacebookAdImageToArray(value) {
      const url = typeof value === 'string'
        ? value
        : (value && (value.url || value.src || value.path)) || '';

      if (url) {
        this.localFunnelResource.facebook_ads[
          this.selectedFacebookAdsImageIndex
        ].images.push(url);

        this.updateFunnelResource();
      }

      if (this.$refs.adImagesModal) this.$refs.adImagesModal.close();
    },
    isFacebookAdTabActive(ad) {
      return this.localFacebookAdsTab === `facebook_ad_tab_${ad.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      facebook_ads: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          copy: {
            required,
            minLength: minLength(3),
          },
          interests: {},
          $trackBy: 'id',
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>

<style scoped>
.gs-facebook-ads__images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  min-width: 0;
}

.gs-facebook-ads__image {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  margin: 0;
  height: 120px;
  padding: 8px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-facebook-ads__image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.gs-facebook-ads__image-remove {
  display: inline-flex;
  position: absolute;
  top: 6px;
  right: 6px;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  color: var(--gs-danger);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.gs-facebook-ads__image-remove:hover {
  background: var(--gs-muted);
}

.gs-facebook-ads__image-remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-facebook-ads__image-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 120px;
  color: var(--gs-primary);
  font-size: 13px;
  font-weight: 600;
  background: var(--gs-primary-soft);
  border: 1px dashed var(--gs-primary);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
  transition: background-color 120ms ease;
}

.gs-facebook-ads__image-add:hover {
  background: var(--gs-surface);
}

.gs-facebook-ads__image-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

/* vue-tags-input renders its own chrome, so the container is re-skinned to
   match the other fields on the card. */
.gs-facebook-ads__tags ::v-deep .vue-tags-input {
  max-width: 100%;
  background: transparent;
}

.gs-facebook-ads__tags ::v-deep .ti-input {
  padding: 6px 8px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-facebook-ads__tags ::v-deep .vue-tags-input.ti-focus .ti-input {
  border-color: var(--gs-primary);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-facebook-ads__tags ::v-deep .ti-tag {
  color: var(--gs-foreground);
  font-size: 13px;
  background: var(--gs-muted);
  border-radius: 999px;
}

.gs-facebook-ads__tags ::v-deep .ti-new-tag-input {
  color: var(--gs-foreground);
  font-size: 14px;
  font-family: inherit;
  background: transparent;
}
</style>
